import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Skeleton,
  Empty,
  Space,
  Select,
  Button,
  Tooltip,
  Typography,
  Tag,
} from 'antd';
import {
  CheckSquareOutlined,
  PlayCircleOutlined,
  RobotOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  ClockCircleOutlined,
  ReloadOutlined,
  RiseOutlined,
  WarningOutlined,
  DollarCircleOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import type {
  RunSummary,
  AnalyticsSummary,
  TopFailingScenario,
  CostSummary,
  FlakeRow,
} from '@/api/validationRuns';
import { getValidationAnalyticsSummary } from '@/api/validationRuns';
import { colors } from '@/theme/tokens';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { KpiTile } from '@/components/KpiTile';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { StatusPill } from '@/components/StatusPill';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Health Checks (Analytics & trends) page
// ─────────────────────────────────────────────────────────────────────────────
//
// First-pass dashboard that surfaces real data we already have on the wire,
// rather than the prior "ships in Sprint 3" placeholder. Everything here is
// computed client-side from the existing /api/ValidationRuns/Search payload —
// no new backend endpoints required for v1. As we add per-scenario failure
// breakdowns and a dedicated analytics endpoint, the heavier panels can move
// to server-side aggregation.
//
// What it shows today
//   * Four KPI tiles — runs, scenarios executed, overall pass rate, avg run
//     duration (window selectable: 7 / 30 / 90 days).
//   * Daily pass-rate sparkline — small bar strip, one bar per day, height
//     proportional to scenarios run, color = pass rate band.
//   * Per-agent leaderboard — total runs, total scenarios, pass rate, last
//     run timestamp. Click an agent → filtered Recent runs.
//
// What it doesn't show yet (deliberate)
//   * Most-failing scenarios — would need a server-side group-by on
//     ValidationRunResult.ScenarioId. Add when we wire the analytics
//     controller.
//   * Cost per run — depends on aggregating ai.PromptLog by run id.
//   * Flake heatmap — requires per-scenario time-series. Deferred.
// ─────────────────────────────────────────────────────────────────────────────

export const ValidationsListPage = observer(function ValidationsListPage() {
  const {
    validationRunsStore: store,
    merchantContextStore: ctx,
    themeStore,
  } = useStore();
  const isDark = themeStore.mode === 'dark';
  const navigate = useNavigate();

  useEffect(() => {
    if (ctx.selectedMerchantId != null && store.runs.length === 0 && !store.isLoading) {
      void store.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.selectedMerchantId]);

  // Analytics summary — separate state from the runs store because it's a
  // dashboard-only payload and should re-fetch when the time window changes.
  const [analytics, setAnalytics] = useState<AnalyticsSummary | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (ctx.selectedMerchantId == null) return;
    let cancelled = false;
    setAnalyticsLoading(true);
    void getValidationAnalyticsSummary(store.days)
      .then((s) => {
        if (!cancelled) setAnalytics(s);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[HealthChecks] analytics summary failed', err);
        if (!cancelled) setAnalytics(null);
      })
      .finally(() => {
        if (!cancelled) setAnalyticsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ctx.selectedMerchantId, store.days]);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<CheckSquareOutlined />}
          title="Pick a merchant to begin"
          description="Health checks are scoped to a single merchant via ai.AiAgent."
          size="lg"
        />
      </SectionCard>
    );
  }

  // ── Aggregations (memoized) ─────────────────────────────────────────────
  // We deliberately do this in the render pass rather than as MobX computeds
  // so the page stays self-contained — the validationRunsStore is shared
  // with Recent runs and shouldn't pick up analytics-flavored helpers it
  // doesn't otherwise need.
  const agg = useMemo(() => computeAggregates(store.runs), [store.runs]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="green"
        icon={<CheckSquareOutlined />}
        title={
          <Space size={6} align="center">
            <span>Health Checks</span>
            <HelpHotspot topic="validations.overview" />
          </Space>
        }
        subtitle={`Trend dashboard for the last ${store.days} days. Backed by aivalidation.ValidationRun rows for this merchant.`}
        actions={
          <Space>
            <Select
              style={{ width: 160 }}
              value={store.days}
              onChange={(v) => store.setDays(v)}
              options={[
                { value: 7, label: 'Last 7 days' },
                { value: 30, label: 'Last 30 days' },
                { value: 90, label: 'Last 90 days' },
              ]}
            />
            <Button
              icon={<ReloadOutlined />}
              loading={store.isLoading}
              onClick={() => void store.fetch()}
            >
              Refresh
            </Button>
            <Link to="/runs">
              <Button type="primary" icon={<PlayCircleOutlined />}>
                Recent runs
              </Button>
            </Link>
          </Space>
        }
      />

      {/* Loading state on first paint — KPI tiles + table skeletons */}
      {store.isLoading && store.runs.length === 0 ? (
        <SectionCard>
          <Skeleton active paragraph={{ rows: 6 }} />
        </SectionCard>
      ) : store.runs.length === 0 ? (
        <SectionCard>
          <BrandedEmpty
            icon={<CheckSquareOutlined />}
            title="No runs in this window"
            description="Trigger a run from the Scenario library, or widen the time window using the dropdown above."
            size="md"
          />
        </SectionCard>
      ) : (
        <>
          {/* KPI Row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 12,
            }}
          >
            <KpiTile
              accent="ink"
              label="Runs"
              value={agg.totalRuns}
              hint={`${agg.distinctAgents} agent${agg.distinctAgents === 1 ? '' : 's'} exercised`}
              icon={<PlayCircleOutlined />}
              onClick={() => navigate('/runs')}
            />
            <KpiTile
              accent="violet"
              label="Scenarios executed"
              value={agg.totalScenarios.toLocaleString()}
              hint={`${agg.totalPassed.toLocaleString()} passed · ${agg.totalFailed.toLocaleString()} failed`}
              icon={<CheckSquareOutlined />}
            />
            <KpiTile
              accent={agg.passRate >= 95 ? 'green' : agg.passRate >= 80 ? 'amber' : 'amber'}
              label="Overall pass rate"
              value={`${agg.passRate}%`}
              hint={
                agg.passRate >= 95
                  ? 'Healthy — at or above target'
                  : agg.passRate >= 80
                  ? 'Watch — below 95% target'
                  : 'Action — well below target'
              }
              icon={<RiseOutlined />}
            />
            <KpiTile
              accent="ink"
              label="Avg run duration"
              value={formatDuration(agg.avgElapsedMs)}
              hint={`Median ${formatDuration(agg.medianElapsedMs)}`}
              icon={<ClockCircleOutlined />}
            />
          </div>

          {/* Daily pass-rate sparkline */}
          <SectionCard
            title="Pass rate by day"
            subtitle={`One bar per day for the last ${store.days} days. Height = scenarios run; color = pass rate band.`}
          >
            <DailySparkline byDay={agg.byDay} isDark={isDark} days={store.days} />
          </SectionCard>

          {/* Per-agent leaderboard */}
          <SectionCard
            title="By agent"
            subtitle="Runs, scenarios, and pass rate per agent in this window. Click a row for that agent's run feed."
            noPadding
          >
            <AgentLeaderboard
              agents={agg.byAgent}
              isDark={isDark}
              onSelectAgent={(agentId) => {
                store.setAgentFilter(agentId);
                navigate('/runs');
              }}
            />
          </SectionCard>

          {/* ── Top failing scenarios ────────────────────────────────── */}
          <SectionCard
            title="Most failing scenarios"
            subtitle="Where to spend triage time first. Scenarios ranked by failure count in this window."
            noPadding
          >
            <TopFailingPanel
              isLoading={analyticsLoading}
              rows={analytics?.topFailingScenarios ?? []}
              isDark={isDark}
            />
          </SectionCard>

          {/* ── Cost overview ───────────────────────────────────────── */}
          <SectionCard
            title="Cost overview"
            subtitle="Estimated Foundry / OpenAI spend on validation runs in this window. Lights up once cost telemetry is being captured."
          >
            <CostPanel
              isLoading={analyticsLoading}
              cost={analytics?.cost ?? null}
              isDark={isDark}
            />
          </SectionCard>

          {/* ── Flake heatmap ───────────────────────────────────────── */}
          <SectionCard
            title="Flake heatmap"
            subtitle="Scenarios with mixed or all-failing recent results. Each cell is one run, oldest on the left, newest on the right."
            noPadding
          >
            <FlakeHeatmap
              isLoading={analyticsLoading}
              rows={analytics?.flake ?? []}
              isDark={isDark}
            />
          </SectionCard>
        </>
      )}
    </div>
  );
});

// ── Aggregations ───────────────────────────────────────────────────────────

interface DayBucket {
  date: string;
  scenarios: number;
  passed: number;
}

interface AgentBucket {
  agentId: number;
  agentName: string;
  totalRuns: number;
  totalScenarios: number;
  totalPassed: number;
  totalFailed: number;
  passRate: number;
  lastRunDate: string;
  lastRunPassed: boolean;
}

interface Aggregates {
  totalRuns: number;
  totalScenarios: number;
  totalPassed: number;
  totalFailed: number;
  passRate: number;
  avgElapsedMs: number;
  medianElapsedMs: number;
  distinctAgents: number;
  byDay: DayBucket[];
  byAgent: AgentBucket[];
}

function computeAggregates(runs: RunSummary[]): Aggregates {
  if (runs.length === 0) {
    return {
      totalRuns: 0,
      totalScenarios: 0,
      totalPassed: 0,
      totalFailed: 0,
      passRate: 0,
      avgElapsedMs: 0,
      medianElapsedMs: 0,
      distinctAgents: 0,
      byDay: [],
      byAgent: [],
    };
  }

  let totalScenarios = 0;
  let totalPassed = 0;
  let totalFailed = 0;
  const elapsed: number[] = [];
  const dayMap = new Map<string, DayBucket>();
  const agentMap = new Map<number, AgentBucket>();

  for (const r of runs) {
    totalScenarios += r.totalScenarios;
    totalPassed += r.passedScenarios;
    totalFailed += r.failedScenarios;
    if (r.totalElapsedMs > 0) elapsed.push(r.totalElapsedMs);

    // Day key — strip time, keep YYYY-MM-DD in run-date local TZ.
    const day = new Date(r.runDate).toISOString().slice(0, 10);
    const dayBucket = dayMap.get(day) ?? { date: day, scenarios: 0, passed: 0 };
    dayBucket.scenarios += r.totalScenarios;
    dayBucket.passed += r.passedScenarios;
    dayMap.set(day, dayBucket);

    // Per agent.
    const agentBucket = agentMap.get(r.aiAgentId) ?? {
      agentId: r.aiAgentId,
      agentName: r.agentName ?? `Agent #${r.aiAgentId}`,
      totalRuns: 0,
      totalScenarios: 0,
      totalPassed: 0,
      totalFailed: 0,
      passRate: 0,
      lastRunDate: r.runDate,
      lastRunPassed: r.failedScenarios === 0 && r.totalScenarios > 0,
    };
    agentBucket.totalRuns += 1;
    agentBucket.totalScenarios += r.totalScenarios;
    agentBucket.totalPassed += r.passedScenarios;
    agentBucket.totalFailed += r.failedScenarios;
    if (r.runDate > agentBucket.lastRunDate) {
      agentBucket.lastRunDate = r.runDate;
      agentBucket.lastRunPassed = r.failedScenarios === 0 && r.totalScenarios > 0;
    }
    agentMap.set(r.aiAgentId, agentBucket);
  }

  // Finalize per-agent pass rate, sort by activity.
  const byAgent: AgentBucket[] = [...agentMap.values()].map((a) => ({
    ...a,
    passRate:
      a.totalScenarios > 0 ? Math.round((100 * a.totalPassed) / a.totalScenarios) : 0,
  }));
  byAgent.sort((x, y) => y.totalRuns - x.totalRuns);

  // Sort days ascending so the sparkline reads left → right.
  const byDay = [...dayMap.values()].sort((a, b) => a.date.localeCompare(b.date));

  // Median elapsed (used in KPI hint).
  const sortedElapsed = [...elapsed].sort((a, b) => a - b);
  const medianElapsedMs =
    sortedElapsed.length === 0
      ? 0
      : sortedElapsed[Math.floor(sortedElapsed.length / 2)] ?? 0;
  const avgElapsedMs =
    elapsed.length === 0
      ? 0
      : Math.round(elapsed.reduce((s, n) => s + n, 0) / elapsed.length);

  return {
    totalRuns: runs.length,
    totalScenarios,
    totalPassed,
    totalFailed,
    passRate: totalScenarios > 0 ? Math.round((100 * totalPassed) / totalScenarios) : 0,
    avgElapsedMs,
    medianElapsedMs,
    distinctAgents: agentMap.size,
    byDay,
    byAgent,
  };
}

function formatDuration(ms: number): string {
  if (!ms) return '—';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return remSec === 0 ? `${min}m` : `${min}m ${remSec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin === 0 ? `${hr}h` : `${hr}h ${remMin}m`;
}

// ── Daily sparkline ─────────────────────────────────────────────────────────
//
// Pure CSS — no chart lib needed. Each bar's height is proportional to the
// scenarios-run count for that day, color band reflects pass rate. Works
// with any window size from 7 → 90 days because the bar count adapts.

function DailySparkline({
  byDay,
  isDark,
  days,
}: {
  byDay: DayBucket[];
  isDark: boolean;
  days: number;
}) {
  if (byDay.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No data to chart yet."
      />
    );
  }

  const maxScenarios = Math.max(1, ...byDay.map((d) => d.scenarios));
  // Always lay out the full window as columns so an empty day shows as a
  // dim placeholder bar rather than collapsing the chart's left edge.
  const today = new Date();
  const allDays: { date: string; bucket: DayBucket | null }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    allDays.push({ date: key, bucket: byDay.find((b) => b.date === key) ?? null });
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 3,
          height: 120,
          padding: '6px 0 12px',
        }}
      >
        {allDays.map((d) => {
          const bucket = d.bucket;
          const passRate =
            bucket && bucket.scenarios > 0
              ? Math.round((100 * bucket.passed) / bucket.scenarios)
              : null;
          const heightPct =
            bucket && bucket.scenarios > 0
              ? Math.max(6, Math.round((100 * bucket.scenarios) / maxScenarios))
              : 6;
          const fg =
            passRate == null
              ? isDark
                ? colors.slate[700]
                : colors.slate[200]
              : passRate >= 95
              ? colors.green[600]
              : passRate >= 80
              ? colors.amber[600]
              : colors.red[600];

          return (
            <Tooltip
              key={d.date}
              title={
                bucket && bucket.scenarios > 0
                  ? `${formatDateShort(d.date)} · ${bucket.scenarios} scenario${bucket.scenarios === 1 ? '' : 's'} · ${passRate}% pass`
                  : `${formatDateShort(d.date)} · no runs`
              }
            >
              <div
                style={{
                  flex: 1,
                  minWidth: 6,
                  maxWidth: 32,
                  height: `${heightPct}%`,
                  background: fg,
                  borderRadius: 3,
                  opacity: bucket ? 1 : 0.4,
                  transition: 'opacity 120ms ease',
                  cursor: 'default',
                }}
              />
            </Tooltip>
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          fontFamily: "'JetBrains Mono', monospace",
          color: isDark ? colors.slate[500] : colors.slate[500],
          paddingTop: 4,
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <span>{formatDateShort(allDays[0]?.date)}</span>
        <span>{formatDateShort(allDays[allDays.length - 1]?.date)}</span>
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 10, fontSize: 11.5 }}>
        <LegendChip color={colors.green[600]} label="≥ 95% pass" />
        <LegendChip color={colors.amber[600]} label="80–94%" />
        <LegendChip color={colors.red[600]} label="< 80%" />
        <LegendChip color={isDark ? colors.slate[700] : colors.slate[200]} label="No runs" />
      </div>
    </div>
  );
}

function LegendChip({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: 2,
          background: color,
          display: 'inline-block',
        }}
      />
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        {label}
      </Text>
    </span>
  );
}

function formatDateShort(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Per-agent leaderboard ──────────────────────────────────────────────────

function AgentLeaderboard({
  agents,
  isDark,
  onSelectAgent,
}: {
  agents: AgentBucket[];
  isDark: boolean;
  onSelectAgent: (agentId: number) => void;
}) {
  if (agents.length === 0) {
    return (
      <div style={{ padding: '20px 24px' }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No agents in this window." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 110px 130px 130px 180px 40px',
          gap: 10,
          padding: '10px 18px',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <span>Agent</span>
        <span style={{ textAlign: 'right' }}>Runs</span>
        <span style={{ textAlign: 'right' }}>Scenarios</span>
        <span style={{ textAlign: 'right' }}>Pass rate</span>
        <span>Last run</span>
        <span />
      </div>
      {agents.map((a) => (
        <button
          key={a.agentId}
          type="button"
          onClick={() => onSelectAgent(a.agentId)}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 110px 130px 130px 180px 40px',
            gap: 10,
            alignItems: 'center',
            padding: '12px 18px',
            borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
            background: 'transparent',
            border: 'none',
            borderTop: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            cursor: 'pointer',
            textAlign: 'left',
            color: 'inherit',
            transition: 'background 100ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = isDark ? colors.slate[800] : colors.slate[50];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span
              style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                background: isDark ? `${colors.ink[700]}55` : colors.ink[50],
                color: colors.ink[600],
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                flexShrink: 0,
              }}
            >
              <RobotOutlined />
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isDark ? colors.slate[100] : colors.slate[900],
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: 320,
                }}
              >
                {a.agentName}
              </div>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: "'JetBrains Mono', monospace" }}>
                #{a.agentId}
              </Text>
            </div>
          </div>
          <Text
            style={{
              textAlign: 'right',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {a.totalRuns}
          </Text>
          <Text
            style={{
              textAlign: 'right',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              fontVariantNumeric: 'tabular-nums',
              color: isDark ? colors.slate[300] : colors.slate[700],
            }}
          >
            {a.totalScenarios}
            {a.totalFailed > 0 && (
              <Tag
                color="red"
                style={{ marginLeft: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5 }}
              >
                {a.totalFailed} fail
              </Tag>
            )}
          </Text>
          <div style={{ textAlign: 'right' }}>
            <PassRateBadge passRate={a.passRate} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {a.lastRunPassed ? (
              <CheckCircleFilled style={{ color: colors.green[600], fontSize: 14, flexShrink: 0 }} />
            ) : (
              <CloseCircleFilled style={{ color: colors.red[600], fontSize: 14, flexShrink: 0 }} />
            )}
            <Text style={{ fontSize: 12, color: isDark ? colors.slate[300] : colors.slate[600] }}>
              {humanRelative(new Date(a.lastRunDate))}
            </Text>
          </div>
          <span style={{ color: isDark ? colors.slate[500] : colors.slate[400], textAlign: 'right' }}>›</span>
        </button>
      ))}
    </div>
  );
}

function PassRateBadge({ passRate }: { passRate: number }) {
  const tone = passRate >= 95 ? 'green' : passRate >= 80 ? 'amber' : 'red';
  return (
    <StatusPill tone={tone}>
      {passRate}%
    </StatusPill>
  );
}

function humanRelative(when: Date): string {
  const diffSec = Math.max(0, Math.floor((Date.now() - when.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)} d ago`;
  return when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Top failing scenarios panel ────────────────────────────────────────────

function TopFailingPanel({
  isLoading,
  rows,
  isDark,
}: {
  isLoading: boolean;
  rows: TopFailingScenario[];
  isDark: boolean;
}) {
  if (isLoading && rows.length === 0) {
    return (
      <div style={{ padding: '14px 18px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ padding: '20px 24px' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
              No failing scenarios in this window. 🎉
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 220px 110px 110px 130px',
          gap: 10,
          padding: '10px 18px',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <span>Scenario</span>
        <span>Agent</span>
        <span style={{ textAlign: 'right' }}>Fails</span>
        <span style={{ textAlign: 'right' }}>Fail rate</span>
        <span>Last fail</span>
      </div>
      {rows.map((r) => (
        <div
          key={`${r.agentId}:${r.scenarioId}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 220px 110px 110px 130px',
            gap: 10,
            alignItems: 'center',
            padding: '10px 18px',
            borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            <WarningOutlined style={{ color: colors.red[600], fontSize: 13, flexShrink: 0 }} />
            <Text
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12.5,
                fontWeight: 600,
                color: isDark ? colors.slate[100] : colors.slate[900],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {r.scenarioId}
            </Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <RobotOutlined style={{ color: colors.ink[600], fontSize: 12, flexShrink: 0 }} />
            <Text
              style={{
                fontSize: 12.5,
                color: isDark ? colors.slate[300] : colors.slate[700],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {r.agentName}
            </Text>
          </div>
          <Text
            style={{
              textAlign: 'right',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 13,
              fontWeight: 600,
              color: colors.red[600],
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {r.failCount}{' '}
            <span style={{ color: isDark ? colors.slate[500] : colors.slate[400], fontWeight: 400 }}>
              / {r.totalRuns}
            </span>
          </Text>
          <div style={{ textAlign: 'right' }}>
            <StatusPill tone={r.failRate >= 50 ? 'red' : r.failRate >= 20 ? 'amber' : 'slate'}>
              {r.failRate}%
            </StatusPill>
          </div>
          <Text
            style={{
              fontSize: 12,
              color: isDark ? colors.slate[300] : colors.slate[600],
            }}
          >
            {humanRelative(new Date(r.lastFailDate))}
          </Text>
        </div>
      ))}
    </div>
  );
}

// ── Cost panel ──────────────────────────────────────────────────────────────

function CostPanel({
  isLoading,
  cost,
  isDark,
}: {
  isLoading: boolean;
  cost: CostSummary | null;
  isDark: boolean;
}) {
  if (isLoading && !cost) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }
  // No cost data captured yet — friendly explainer rather than scary blank.
  const noData = !cost || cost.runsWithCost === 0;
  if (noData) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: '8px 4px',
          fontSize: 13,
          color: isDark ? colors.slate[300] : colors.slate[600],
        }}
      >
        <DollarCircleOutlined
          style={{ fontSize: 22, color: isDark ? colors.slate[500] : colors.slate[400] }}
        />
        <div>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>No cost telemetry yet</div>
          <div style={{ fontSize: 12.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
            Validation runs in this window have not recorded token costs. This panel will populate
            automatically once the runner starts capturing <code>EstimatedCostUsd</code> per
            scenario.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        <CostFigure
          label="Total spent"
          value={`$${cost.totalUsd.toFixed(2)}`}
          hint={`across ${cost.runsWithCost} run${cost.runsWithCost === 1 ? '' : 's'}`}
          isDark={isDark}
          accent={colors.amber[600]}
        />
        <CostFigure
          label="Avg per run"
          value={`$${cost.avgCostPerRunUsd.toFixed(4)}`}
          hint=""
          isDark={isDark}
          accent={colors.violet[600]}
        />
        <CostFigure
          label="Input tokens"
          value={cost.inputTokens.toLocaleString()}
          hint=""
          isDark={isDark}
          accent={colors.ink[600]}
        />
        <CostFigure
          label="Output tokens"
          value={cost.outputTokens.toLocaleString()}
          hint=""
          isDark={isDark}
          accent={colors.green[600]}
        />
      </div>
      {cost.byAgent.length > 0 && (
        <div>
          <Text
            type="secondary"
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              display: 'block',
              marginBottom: 8,
            }}
          >
            Spend by agent
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {cost.byAgent.map((a) => {
              const pct = cost.totalUsd > 0 ? (a.costUsd / cost.totalUsd) * 100 : 0;
              return (
                <div
                  key={a.agentId}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 80px 110px', gap: 10, alignItems: 'center' }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Text
                      style={{
                        fontSize: 12.5,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {a.agentName}
                    </Text>
                  </div>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: isDark ? colors.slate[700] : colors.slate[100],
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        width: `${Math.max(2, pct)}%`,
                        height: '100%',
                        background: colors.amber[600],
                      }}
                    />
                  </div>
                  <Text
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 12,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    ${a.costUsd.toFixed(4)} · {a.runCount}r
                  </Text>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CostFigure({
  label,
  value,
  hint,
  accent,
  isDark,
}: {
  label: string;
  value: string;
  hint: string;
  accent: string;
  isDark: boolean;
}) {
  return (
    <div
      style={{
        padding: '12px 14px',
        background: isDark ? colors.slate[800] : '#FFFFFF',
        border: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 8,
      }}
    >
      <Text
        type="secondary"
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          display: 'block',
        }}
      >
        {label}
      </Text>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          fontFamily: "'JetBrains Mono', monospace",
          fontVariantNumeric: 'tabular-nums',
          color: isDark ? colors.slate[50] : colors.slate[900],
          marginTop: 2,
        }}
      >
        {value}
      </div>
      {hint && (
        <Text type="secondary" style={{ fontSize: 11.5, display: 'block', marginTop: 2 }}>
          {hint}
        </Text>
      )}
    </div>
  );
}

// ── Flake heatmap ──────────────────────────────────────────────────────────

function FlakeHeatmap({
  isLoading,
  rows,
  isDark,
}: {
  isLoading: boolean;
  rows: FlakeRow[];
  isDark: boolean;
}) {
  if (isLoading && rows.length === 0) {
    return (
      <div style={{ padding: '14px 18px' }}>
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div style={{ padding: '20px 24px' }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span style={{ color: isDark ? colors.slate[300] : colors.slate[600], fontSize: 13 }}>
              No flaky scenarios. Runs are either consistently passing or there's not enough
              history yet.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 200px auto',
          gap: 12,
          padding: '10px 18px',
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: isDark ? colors.slate[400] : colors.slate[500],
          borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[200]}`,
        }}
      >
        <span>Scenario</span>
        <span>Agent</span>
        <span>Recent runs (oldest → newest)</span>
      </div>
      {rows.map((row) => {
        const passes = row.cells.filter((c) => c.passed).length;
        const total = row.cells.length;
        return (
          <div
            key={`${row.agentId}:${row.scenarioId}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 200px auto',
              gap: 12,
              alignItems: 'center',
              padding: '10px 18px',
              borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <ThunderboltOutlined style={{ color: colors.violet[600], fontSize: 13, flexShrink: 0 }} />
              <Text
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: isDark ? colors.slate[100] : colors.slate[900],
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {row.scenarioId}
              </Text>
              <Text type="secondary" style={{ fontSize: 11.5, marginLeft: 6 }}>
                {passes}/{total} pass
              </Text>
            </div>
            <Text
              style={{
                fontSize: 12.5,
                color: isDark ? colors.slate[300] : colors.slate[700],
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <RobotOutlined style={{ color: colors.ink[600], fontSize: 11, marginRight: 6 }} />
              {row.agentName}
            </Text>
            <div style={{ display: 'flex', gap: 3 }}>
              {row.cells.map((c) => (
                <Tooltip
                  key={c.validationRunId}
                  title={`Run #${c.validationRunId} · ${c.passed ? 'pass' : 'fail'} · ${new Date(c.runDate).toLocaleString()}`}
                >
                  <Link
                    to={`/runs/${c.validationRunId}`}
                    style={{
                      display: 'inline-block',
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: c.passed ? colors.green[600] : colors.red[600],
                      cursor: 'pointer',
                    }}
                  />
                </Tooltip>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
